import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import styles from './GanheMaisCorretorView.module.css';
import { Button } from '../../../../components/Button';
import { ButtonBack } from '../../../../components/ButtonBack';
import { HelmetComponent } from '../../../../components/Helmet';
import { Notify } from '../../../../components/Notify';
import { AlertModal } from '../../../../components/AlertModal';

import * as routes from '../../../../constants/Routes';
import { useAuth } from '../../../../hooks/useAuth';
import { useCart } from '../../../../hooks/useCart';
import { usePoints } from '../../../../hooks/usePoints';
import { api } from '../../../../server/api';
import { numberWithCommas } from '../../../../utils/numberWithCommas';
import { IAwards, ILockedBonus } from '../../types';

import imgCoin from '../../../../assets/images/coin.png';
import imgPending from '../../../../assets/images/icon-pontos-pendentes.png';
import imgSafeBox from '../../../../assets/images/safeBox.png';
import imgTotal from '../../../../assets/images/icon-pontos-totais.png';

const formatBRL = (value: number) =>
  `R$ ${numberWithCommas(Math.round(value || 0))}`;

// statusTransfer state machine (backend enum):
//   FINALIZADO          -> released, counts as Resgatado
//   FALHA / DEVOLVIDO   -> rejected, backend returns value to Disponíveis
//   absent / CRIADO / PENDENTE -> still in process = "Aguardando liberação"
// A rescue is "hanging" (em aprovação) until it is FINALIZADO; rejected ones are
// excluded because the backend already moved them back to Disponíveis.
const REJECTED_TRANSFER = ['FALHA', 'DEVOLVIDO'];
const isPendingApproval = (rescue: any) =>
  rescue.statusTransfer !== 'FINALIZADO' &&
  !REJECTED_TRANSFER.includes(rescue.statusTransfer);

const getRescueStatus = (rescue: any) => {
  if (!rescue.statusTransfer) {
    return { label: 'Aguardando liberação', cls: styles.pillGray };
  }
  if (
    rescue.statusTransfer === 'FALHA' ||
    rescue.statusTransfer === 'DEVOLVIDO'
  ) {
    return { label: rescue.statusTransfer, cls: styles.pillRed };
  }
  return { label: rescue.statusTransfer, cls: styles.pillGreen };
};

export function GanheMaisCorretorView() {
  const { client, player } = useAuth();
  const { availableToRedeem, redeemedPoints, totalPoints, getPoints } =
    usePoints();
  const { cart, addProductToCart, clearCart } = useCart();
  const navigate = useNavigate();

  const [redeemAward, setRedeemAward] = useState<IAwards | null>(null);
  const [rescues, setRescues] = useState<any[]>([]);
  const [lockedBonuses, setLockedBonuses] = useState<ILockedBonus[]>([]);
  const [serviceAlertModalIsOpen, setServiceAlertModalIsOpen] = useState(false);

  useEffect(() => {
    getAwards();
    getOngoingRescues();
    getLockedBonus();
  }, []);

  // GanheMaisCorretorView is the start of the ganhemaiscorretor flow, so the cart should always be
  // empty here. A leftover cart means a previous redemption was abandoned
  // (player navigated away instead of cancelling). CartContext would repopulate
  // it from sessionStorage and then handleStartRescue blocks on cart.length > 0
  // ("esvaziar o carrinho"). Check sessionStorage rather than the cart state:
  // CartContext may not have rehydrated the state yet at this mount.
  useEffect(() => {
    if (sessionStorage.getItem('cart')) {
      clearCart();
    }
  }, []);

  useEffect(() => {
    if (player && client && cart.length === 0) {
      getPoints(client.clientUrl, player.id);
    }
  }, [player, client]);

  const minValue = useMemo(() => {
    const min = Number(redeemAward?.paymentValueMinimun);
    return Number.isFinite(min) && min > 0 ? min : 150;
  }, [redeemAward]);

  const getAwards = async () => {
    try {
      const parameters = {
        searchFunctionality: 'getNotGamifiedAwards',
        userType: 'player',
        seasonId: false,
        clientId: client.id,
        clientUrl: client.clientUrl,
        playerId: player?.id,
      };

      const { data } = await api.post(`/search`, parameters);

      if (data.success) {
        const awards: IAwards[] = data.data.awards;
        // the points -> money conversion voucher
        const variable = awards.find(
          (award) => award.valueType && award.valueType.value === 'variable'
        );
        setRedeemAward(variable || null);
      }
    } catch (error) {
      console.log('getAwards error', error);
    }
  };

  const getOngoingRescues = async () => {
    try {
      const parameters = {
        searchFunctionality: 'getPlayerRescues',
        userType: 'player',
        playerId: player.id,
        clientId: client.id,
      };

      const { data } = await api.post('/search', parameters);
      if (data.success && data.data.rescuesSnap) {
        const sorted = [...data.data.rescuesSnap].sort(
          (a, b) => Number(b.createdAt) - Number(a.createdAt)
        );
        // keep the full list: the "Comissão em aprovação" total sums every
        // hanging rescue, while the ongoing card renders only the latest few
        setRescues(sorted);
      }
    } catch (error) {
      console.log('getOngoingRescues error', error);
    }
  };

  // Locked bonus is the source of truth for the bonus banner. The API only
  // returns LOCKED entries; once released the points are credited and the bonus
  // disappears from the response, so an empty array means "show nothing".
  const getLockedBonus = async () => {
    try {
      const { data } = await api.get(
        `/redemption-policy/${player?.id}/locked-bonus`
      );
      setLockedBonuses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log('getLockedBonus error', error);
      setLockedBonuses([]);
    }
  };

  const lockedBonusTotal = useMemo(
    () => lockedBonuses.reduce((sum, b) => sum + (Number(b.value) || 0), 0),
    [lockedBonuses]
  );

  const handleStartRescue = () => {
    if (!redeemAward) {
      return Notify('Resgate indisponível no momento', 'warn');
    }
    if (availableToRedeem < minValue) {
      return Notify(
        `O valor mínimo para resgate é ${formatBRL(minValue)}`,
        'warn'
      );
    }
    if (cart.length > 0) {
      return Notify('Para resgatar é necessário esvaziar o carrinho', 'warn');
    }
    // The variable prize already redeems the full available balance by default.
    const result = addProductToCart(redeemAward);
    if (
      typeof result === 'string' &&
      result === 'Impossivel resgatar valores acima de 15000'
    ) {
      setServiceAlertModalIsOpen(true);
      return;
    }
    // Single cash award — skip the cart screen, go straight to confirmation.
    navigate(routes.CONFIRM_RESCUE);
  };

  // Sum of every hanging rescue. The backend already moved these values out of
  // availableToRedeem and (wrongly, for ganhemaiscorretor) into redeemedPoints on rescue
  // start, so we surface them here and back them out of Resgatados below.
  const emAprovacao = useMemo(
    () =>
      rescues
        .filter(isPendingApproval)
        .reduce((sum, r) => sum + Number(r.redeemedPoints || 0), 0),
    [rescues]
  );

  // Resgatados = only the rescues that actually went through. Subtract the
  // hanging amount the backend prematurely counted as redeemed. Rejected
  // rescues (FALHA/DEVOLVIDO) are returned to Disponíveis by the backend, so
  // availableToRedeem needs no adjustment here.
  const resgatados = Math.max(0, (redeemedPoints || 0) - emAprovacao);

  const canRedeem = availableToRedeem >= minValue;

  return (
    <div className={styles.wrapper}>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Resgate de prêmios'}
      />

      {/* navegação */}
      <div className="py-3 fixed-navigation">
        <div className="row">
          <div className="col">
            <ButtonBack title="Comissões" />
          </div>
        </div>
      </div>
      {/* fim navegação */}

      {/* stat cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <img className={styles.statImg} src={imgCoin} alt="disponíveis" />
          <p className={styles.statLabel}>Disponíveis</p>
          <p className={styles.statValue}>{formatBRL(availableToRedeem)}</p>
          <span className={`${styles.statPill} ${styles.pillGreen}`}>
            ✓ liberados
          </span>
        </div>

        <div className={styles.statCard}>
          <img className={styles.statImg} src={imgPending} alt="pendentes" />
          <p className={styles.statLabel}>Comissão em aprovação</p>
          <p className={styles.statValue}>{formatBRL(emAprovacao)}</p>
          <span className={`${styles.statPill} ${styles.pillYellow}`}>
            em análise
          </span>
        </div>

        <div className={styles.statCard}>
          <img className={styles.statImg} src={imgSafeBox} alt="resgatados" />
          <p className={styles.statLabel}>Resgatados</p>
          <p className={styles.statValue}>{formatBRL(resgatados)}</p>
        </div>

        <div className={styles.statCard}>
          <img
            className={styles.statImg}
            src={imgTotal}
            alt="total acumulado"
          />
          <p className={styles.statLabel}>Total acumulado</p>
          <p className={styles.statValue}>{formatBRL(totalPoints)}</p>
        </div>
      </div>

      {/* resgate em andamento */}
      <div className={styles.sectionHeader}>
        <p className={styles.sectionTitle}>
          <i
            className="material-icons-outlined"
            style={{ color: client?.primaryColor }}
          >
            schedule
          </i>
          Resgate em andamento
        </p>
        <span
          className={styles.link}
          style={{ color: client?.primaryColor }}
          onClick={() => navigate(routes.MY_RESCUES)}
        >
          Acompanhar
        </span>
      </div>

      <div className={styles.ongoingCard}>
        {rescues.length === 0 ? (
          <p className={styles.empty}>Nenhum resgate em andamento.</p>
        ) : (
          rescues.slice(0, 3).map((rescue) => {
            const status = getRescueStatus(rescue);
            return (
              <div
                key={rescue.id || rescue.createdAt}
                className={styles.ongoingRow}
              >
                <div className={styles.ongoingInfo}>
                  <div className={styles.ongoingTop}>
                    <p className={styles.ongoingId}>
                      {rescue.createdAt
                        ? moment(Number(rescue.createdAt)).format('DD/MM/YYYY')
                        : '—'}
                    </p>
                    <span className={`${styles.statPill} ${status.cls}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className={styles.ongoingMeta}>
                    {formatBRL(rescue.redeemedPoints)}
                  </p>
                </div>
                <span
                  className={styles.link}
                  style={{ color: client?.primaryColor }}
                  onClick={() =>
                    navigate(`${routes.RESCUE_DETAILS}/${rescue.id}`)
                  }
                >
                  Ver steps →
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* solicitar resgate */}
      <div className={styles.sectionHeader}>
        <p className={styles.sectionTitle}>
          <i
            className="material-icons-outlined"
            style={{ color: client?.primaryColor }}
          >
            account_balance_wallet
          </i>
          Solicitar resgate
        </p>
      </div>

      <div className={styles.redeemCard}>
        <p className={styles.redeemLabel}>Valor a resgatar</p>
        <div className={styles.redeemValueRow}>
          <p
            className={styles.redeemValue}
            style={{ color: client?.primaryColor }}
          >
            {formatBRL(availableToRedeem)}
          </p>
          <span className={styles.redeemAvailable}>valor total disponível</span>
        </div>

        {lockedBonusTotal > 0 && (
          <p className={styles.bonusLocked}>
            <i className="material-icons-outlined">lock</i>
            <span>
              Falta pouco! Bata os critérios e desbloqueie{' '}
              <strong>{formatBRL(lockedBonusTotal)}</strong> de bônus no seu
              resgate
            </span>
          </p>
        )}

        <div className={styles.notice}>
          <i className="material-icons-outlined">info</i>
          <span>
            Para resgatar, você precisará emitir uma{' '}
            <strong>Nota Fiscal de serviço</strong>. Você será guiado no
            processo.
          </span>
        </div>

        <Button
          text="Iniciar resgate"
          icon="bolt"
          color="primary"
          isLoading={false}
          onClick={handleStartRescue}
          disabled={!canRedeem}
          style={{ width: '100%' }}
        />
      </div>

      <AlertModal
        modalIsOpen={serviceAlertModalIsOpen}
        toggleModal={() => setServiceAlertModalIsOpen(false)}
        title="OPS!"
        body="O limite diário de resgate é de R$ 15 mil. Mas não se preocupe: volte amanhã e resgate o valor restante."
      />
    </div>
  );
}
